import React, { useEffect, useRef, useState, useCallback } from 'react';
import { request, followSession, fmt, API_URL } from '../api.js';
import { Link, navigate, useMe } from '../router.jsx';

export function useLoad(path, deps = []) {
  const [state, setState] = useState({ data: null, error: null, loading: true });
  const load = useCallback(() => {
    setState(s => ({ ...s, loading: true }));
    request(path).then(data => setState({ data, error: null, loading: false }), e => setState({ data: null, error: e.message, loading: false }));
  }, [path]);
  useEffect(load, [load, ...deps]);
  return { ...state, reload: load };
}

export const Status = ({ s }) => <span className={`pill ${{ succeeded: 'ok', failed: 'bad', cancelled: '', running: 'accent', starting: 'accent', queued: 'warn' }[s] ?? ''}`}>{s}</span>;
export const Err = ({ error }) => (error ? <div className="error">{error}</div> : null);
export const Empty = ({ children }) => <div className="card muted">{children}</div>;

export function Account({ reloadMe }) {
  const me = useMe();
  const usage = useLoad('/v1/me/usage?days=30');
  const u = me.usage;
  const pct = Math.min(100, Math.round((u.month_tokens / Math.max(u.allowance_tokens, 1)) * 100));
  const days = {};
  for (const r of usage.data?.rows || []) { const k = r.day.slice(0, 10); days[k] = (days[k] || 0) + r.input + r.output + r.cache_read; }
  const series = Array.from({ length: 30 }, (_, i) => { const d = new Date(Date.now() - (29 - i) * 86400e3).toISOString().slice(0, 10); return days[d] || 0; });
  const max = Math.max(1, ...series);
  const models = {};
  for (const r of usage.data?.rows || []) { models[r.model] ??= { tokens: 0, cost: 0, requests: 0 }; models[r.model].tokens += r.input + r.output + r.cache_read; models[r.model].cost += r.cost_usd; models[r.model].requests += r.requests; }
  return (
    <>
      <div className="page-head"><div><h1>Plan &amp; usage</h1><p className="muted">{me.user.email} · resets {new Date(u.resets_at).toLocaleDateString()}</p></div><button className="btn" onClick={reloadMe}>Refresh</button></div>
      <div className="grid">
        <div className="card stat"><div className="label">Plan</div><div className="value">{me.plan.name}</div><div className="sub">{me.plan.requests_per_min} req/min · {me.plan.max_sessions} cloud sessions · {me.plan.max_schedules} schedules</div></div>
        <div className="card stat"><div className="label">Tokens this month</div><div className="value">{fmt.tokens(u.month_tokens)}</div><div className="meter"><i style={{ width: `${pct}%` }} /></div><div className="sub">{pct}% of {fmt.tokens(u.allowance_tokens)}</div></div>
        <div className="card stat"><div className="label">Requests</div><div className="value">{u.month_requests}</div><div className="sub">{fmt.usd(u.month_cost_usd)} at list price</div></div>
        <div className="card stat"><div className="label">Usage credits</div><div className="value">{fmt.usd(me.credits_cents / 100)}</div><div className="sub">used after the monthly allowance</div></div>
      </div>
      <h2>Last 30 days</h2>
      <div className="card"><div className="bars" aria-label="Tokens per day">{series.map((v, i) => <i key={i} style={{ height: `${(v / max) * 100}%` }} title={`${fmt.tokens(v)} tokens`} />)}</div></div>
      <h2>By model</h2>
      <Err error={usage.error} />
      {Object.keys(models).length ? (
        <div className="table-wrap"><table><thead><tr><th>Model</th><th>Requests</th><th>Tokens</th><th>Cost</th></tr></thead>
          <tbody>{Object.entries(models).sort((a, b) => b[1].tokens - a[1].tokens).map(([m, v]) => <tr key={m}><td className="mono">{m}</td><td>{v.requests}</td><td>{fmt.tokens(v.tokens)}</td><td>{fmt.usd(v.cost)}</td></tr>)}</tbody></table></div>
      ) : <Empty>No usage yet. In Forge Code, run <code>/login</code>, choose <b>Forge Cloud account</b>, then use any Claude model.</Empty>}
      <h2>Upgrade</h2>
      <div className="card"><p>Plans: <b>Free</b> (5M tokens/month), <b>Pro</b> (50M) and <b>Team</b> (250M). Payment isn't connected yet: an admin changes plans and grants credits from the admin console.</p></div>
    </>
  );
}

export function Tokens() {
  const list = useLoad('/v1/me/tokens');
  const [name, setName] = useState('');
  const [created, setCreated] = useState(null);
  const [error, setError] = useState(null);
  const create = async e => {
    e.preventDefault();
    try { setCreated(await request('/v1/me/tokens', { method: 'POST', json: { name } })); setName(''); list.reload(); } catch (err) { setError(err.message); }
  };
  const revoke = async id => { if (confirm('Revoke this token? Anything using it stops working.')) { await request(`/v1/me/tokens/${id}`, { method: 'DELETE' }); list.reload(); } };
  return (
    <>
      <div className="page-head"><div><h1>Tokens</h1><p className="muted">Forge Code gets one automatically from <code>/login</code>. Create one here for CI (<code>FORGE_CLOUD_TOKEN</code>).</p></div></div>
      <form className="row form" onSubmit={create}><input placeholder="Token name, e.g. GitHub Actions" value={name} onChange={e => setName(e.target.value)} required style={{ flex: 1 }} /><button className="btn primary">Create token</button></form>
      {created && <div className="notice">Copy it now; it won't be shown again:<pre className="mono" style={{ userSelect: 'all', margin: '8px 0 0' }}>{created.token}</pre></div>}
      <Err error={error || list.error} />
      <h2>Your tokens</h2>
      <div className="table-wrap"><table><thead><tr><th>Name</th><th>Prefix</th><th>Created</th><th>Last used</th><th /></tr></thead>
        <tbody>{(list.data?.tokens || []).map(t => (
          <tr key={t.id}><td>{t.name}</td><td className="mono">{t.prefix}…</td><td>{fmt.ago(t.created_at)}</td><td>{fmt.ago(t.last_used_at)}</td>
            <td>{t.revoked_at ? <span className="pill">revoked</span> : <button className="btn danger" onClick={() => revoke(t.id)}>Revoke</button>}</td></tr>
        ))}</tbody></table></div>
    </>
  );
}

export function Sessions() {
  const list = useLoad('/v1/sessions');
  const [form, setForm] = useState({ prompt: '', repo_url: '', branch: '', model: 'claude-opus-5-5' });
  const [error, setError] = useState(null);
  const start = async e => {
    e.preventDefault();
    setError(null);
    try {
      const s = await request('/v1/sessions', { method: 'POST', json: { prompt: form.prompt, model: form.model, ...(form.repo_url && { repo_url: form.repo_url }), ...(form.branch && { branch: form.branch }) } });
      navigate(`/sessions/${s.id}`);
    } catch (err) { setError(err.message); }
  };
  return (
    <>
      <div className="page-head"><div><h1>Cloud sessions</h1><p className="muted">Forge Code running in an E2B sandbox. From the terminal: <code>/teleport cloud &lt;prompt&gt;</code>.</p></div></div>
      <form className="card form" onSubmit={start} style={{ maxWidth: 'none' }}>
        <label htmlFor="p">What should Forge do?</label>
        <textarea id="p" required value={form.prompt} onChange={e => setForm({ ...form, prompt: e.target.value })} placeholder="Upgrade the test runner and fix whatever breaks" />
        <div className="row" style={{ alignItems: 'flex-end' }}>
          <div style={{ flex: 3, minWidth: 240 }}><label htmlFor="r">Repository (https, optional)</label><input id="r" value={form.repo_url} onChange={e => setForm({ ...form, repo_url: e.target.value })} placeholder="https://github.com/you/app.git" /></div>
          <div style={{ flex: 1, minWidth: 120 }}><label htmlFor="b">Branch</label><input id="b" value={form.branch} onChange={e => setForm({ ...form, branch: e.target.value })} placeholder="main" /></div>
          <div style={{ flex: 1, minWidth: 160 }}><label htmlFor="m">Model</label><select id="m" value={form.model} onChange={e => setForm({ ...form, model: e.target.value })}>{['claude-opus-5-5', 'claude-sonnet-5-5', 'claude-haiku-5-5', 'claude-fable-5-1'].map(m => <option key={m}>{m}</option>)}</select></div>
        </div>
        <div className="row" style={{ marginTop: 14 }}><button className="btn primary">Start cloud session</button></div>
        <Err error={error} />
      </form>
      <h2>Recent</h2>
      <Err error={list.error} />
      {list.data?.sessions?.length ? (
        <div className="table-wrap"><table><thead><tr><th>Task</th><th>Status</th><th>Repository</th><th>Model</th><th>Started</th></tr></thead>
          <tbody>{list.data.sessions.map(s => <tr key={s.id}><td className="wrap"><Link to={`/sessions/${s.id}`}>{s.title}</Link></td><td><Status s={s.status} /></td><td className="mono">{s.repo_url ? s.repo_url.replace(/^https:\/\//, '') : '—'}</td><td className="mono">{s.model}</td><td>{fmt.ago(s.created_at)}</td></tr>)}</tbody></table></div>
      ) : !list.loading && <Empty>No cloud sessions yet.</Empty>}
    </>
  );
}

export function SessionDetail({ id }) {
  const s = useLoad(`/v1/sessions/${id}`);
  const [lines, setLines] = useState([]);
  const [live, setLive] = useState(true);
  const box = useRef(null);
  useEffect(() => {
    const ctl = new AbortController();
    followSession(id, ev => {
      setLines(l => [...l, ev]);
      if (ev.event === 'status' || ev.event === 'end') s.reload();
      if (ev.event === 'end') setLive(false);
    }, ctl.signal).catch(() => setLive(false));
    return () => ctl.abort();
  }, [id]);
  useEffect(() => { box.current?.scrollTo(0, box.current.scrollHeight); }, [lines]);
  const cancel = async () => { await request(`/v1/sessions/${id}/cancel`, { method: 'POST' }); s.reload(); };
  const d = s.data;
  return (
    <>
      <div className="page-head">
        <div><h1>{d?.title || 'Cloud session'}</h1><p className="muted mono">{id}{d?.sandbox_id ? ` · sandbox ${d.sandbox_id}` : ''}</p></div>
        <div className="row">{d && <Status s={d.status} />}{d && ['queued', 'starting', 'running'].includes(d.status) && <button className="btn danger" onClick={cancel}>Cancel</button>}</div>
      </div>
      <Err error={s.error} />
      {d && <div className="grid" style={{ marginBottom: 16 }}>
        <div className="card stat"><div className="label">Repository</div><div className="mono">{d.repo_url || '—'}{d.branch ? `@${d.branch}` : ''}</div></div>
        <div className="card stat"><div className="label">Model</div><div className="mono">{d.model}</div></div>
        <div className="card stat"><div className="label">Result</div><div>{d.result_teleport_id ? <>In the terminal: <code>/teleport pull {d.result_teleport_id}</code></> : d.status === 'failed' ? d.error || `exit ${d.exit_code}` : '—'}</div></div>
      </div>}
      {d?.repo_url && d.status === 'succeeded' && <p><a href="#" onClick={async e => { e.preventDefault(); const r = await request(`/v1/sessions/${id}/diff`, { raw: true }); const t = await r.text(); const w = window.open(); w.document.body.innerText = r.ok ? t : 'No diff'; }}>View the diff</a></p>}
      <h2>Output {live && <span className="pill accent">live</span>}</h2>
      <div className="log" ref={box}>
        {lines.map((l, i) => <div key={i} className={l.event === 'command' ? 'cmd' : l.event === 'stderr' || l.event === 'error' ? 'err' : l.event === 'stdout' ? '' : 'sys'}>
          {l.event === 'command' ? `$ ${l.data}` : l.event === 'stdout' || l.event === 'stderr' ? l.data : `[${l.event}] ${l.data}`}
        </div>)}
        {!lines.length && <div className="sys">Waiting for output…</div>}
      </div>
    </>
  );
}

export function Schedules() {
  const list = useLoad('/v1/schedules');
  const [form, setForm] = useState({ name: '', prompt: '', kind: 'cron', every: '1d', cron: '7 9 * * 1-5', at: '', repo_url: '' });
  const [error, setError] = useState(null);
  const add = async e => {
    e.preventDefault();
    setError(null);
    try {
      await request('/v1/schedules', { method: 'POST', json: { prompt: form.prompt, ...(form.name && { name: form.name }), ...(form.kind === 'every' ? { every: form.every } : form.kind === 'once' ? { run_at: new Date(form.at).toISOString() } : { cron: form.cron, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone }), ...(form.repo_url && { repo_url: form.repo_url }) } });
      setForm({ ...form, name: '', prompt: '' });
      list.reload();
    } catch (err) { setError(err.message); }
  };
  const act = async (s, what) => {
    if (what === 'toggle') await request(`/v1/schedules/${s.id}`, { method: 'PATCH', json: { enabled: !s.enabled } });
    if (what === 'run') { const r = await request(`/v1/schedules/${s.id}/run`, { method: 'POST' }); navigate(`/sessions/${r.id}`); return; }
    if (what === 'delete' && confirm(`Delete the routine "${s.name || s.prompt.slice(0, 40)}"?`)) await request(`/v1/schedules/${s.id}`, { method: 'DELETE' });
    list.reload();
  };
  return (
    <>
      <div className="page-head"><div><h1>Routines</h1><p className="muted">Prompts that run as cloud sessions on a schedule, at most once an hour, or once at a set time. From Forge Code: <code>/schedule</code>.</p></div></div>
      <form className="card" onSubmit={add}>
        <label htmlFor="sn">Name</label>
        <input id="sn" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Weekly PR review reminder" />
        <label htmlFor="sp">Prompt</label>
        <textarea id="sp" required value={form.prompt} onChange={e => setForm({ ...form, prompt: e.target.value })} placeholder="Check yesterday's CI failures and open a summary" />
        <div className="row" style={{ alignItems: 'flex-end' }}>
          <div><label htmlFor="sk">When</label><select id="sk" value={form.kind} onChange={e => setForm({ ...form, kind: e.target.value })}><option value="cron">Cron</option><option value="every">Every…</option><option value="once">Once</option></select></div>
          {form.kind === 'every'
            ? <div><label htmlFor="se">Interval</label><input id="se" value={form.every} onChange={e => setForm({ ...form, every: e.target.value })} placeholder="1h, 6h, 1d" /></div>
            : form.kind === 'once'
              ? <div><label htmlFor="sa">Date and time</label><input id="sa" type="datetime-local" required value={form.at} onChange={e => setForm({ ...form, at: e.target.value })} /></div>
              : <div><label htmlFor="sc">Cron (local time)</label><input id="sc" className="mono" value={form.cron} onChange={e => setForm({ ...form, cron: e.target.value })} /></div>}
          <div style={{ flex: 1, minWidth: 240 }}><label htmlFor="sr">Repository (optional)</label><input id="sr" value={form.repo_url} onChange={e => setForm({ ...form, repo_url: e.target.value })} placeholder="https://github.com/you/app.git" /></div>
          <button className="btn primary">Create routine</button>
        </div>
        <Err error={error} />
      </form>
      <h2>Your routines</h2>
      <Err error={list.error} />
      {list.data?.schedules?.length ? (
        <div className="table-wrap"><table><thead><tr><th>Routine</th><th>When</th><th>Next run</th><th>Last run</th><th /></tr></thead>
          <tbody>{list.data.schedules.map(s => (
            <tr key={s.id}><td className="wrap"><b>{s.name || s.prompt.slice(0, 60)}</b><div className="muted">{s.prompt.slice(0, 140)}</div></td><td className="mono">{s.run_at ? `once, ${fmt.date(s.run_at)}` : s.cron ? `${s.cron} ${s.timezone}` : `every ${s.every_ms >= 86400e3 ? `${s.every_ms / 86400e3}d` : s.every_ms >= 3600e3 ? `${s.every_ms / 3600e3}h` : `${s.every_ms / 60e3}m`}`}</td>
              <td>{s.enabled ? fmt.date(s.next_run_at) : <span className="pill">paused</span>}</td><td>{fmt.ago(s.last_run_at)}</td>
              <td className="row"><button className="btn" onClick={() => act(s, 'run')}>Run now</button><button className="btn" onClick={() => act(s, 'toggle')}>{s.enabled ? 'Pause' : 'Resume'}</button><button className="btn danger" onClick={() => act(s, 'delete')}>Delete</button></td></tr>
          ))}</tbody></table></div>
      ) : !list.loading && <Empty>No routines yet.</Empty>}
    </>
  );
}

export function Shares() {
  const list = useLoad('/v1/shares');
  const del = async id => { if (confirm('Delete this share? The link stops working.')) { await request(`/v1/shares/${id}`, { method: 'DELETE' }); list.reload(); } };
  return (
    <>
      <div className="page-head"><div><h1>Shares</h1><p className="muted">Conversations shared with <code>/share</code> in Forge Code. Stored in Cloudflare R2.</p></div></div>
      <Err error={list.error} />
      {list.data?.shares?.length ? (
        <div className="table-wrap"><table><thead><tr><th>Title</th><th>Link</th><th>Views</th><th>Size</th><th>Created</th><th /></tr></thead>
          <tbody>{list.data.shares.map(s => <tr key={s.id}><td className="wrap">{s.title}</td><td><Link to={`/s/${s.id}`}>/s/{s.id}</Link></td><td>{s.views}</td><td>{(s.bytes / 1024).toFixed(1)} KB</td><td>{fmt.ago(s.created_at)}</td><td><button className="btn danger" onClick={() => del(s.id)}>Delete</button></td></tr>)}</tbody></table></div>
      ) : !list.loading && <Empty>Nothing shared yet.</Empty>}
    </>
  );
}

export { API_URL };
